import React from "react";
import {withGoogleMap, GoogleMap} from "react-google-maps";
import {MAP} from "react-google-maps/lib/constants";
import {mapContainer} from "../../contexts/map";
import {Subscribe} from "../../contexts/app";
import {arrayToPoint} from "../../lib/google-maps";
import mapConfig from "./map-config";
import "./style.scss";

// TODO: This should not attach globally. Use context,
// or something cleaner. Its main use is to connect to
// the ImageOverlay component.

// Passing the google maps reference to non react-google-maps
// is not really feasible because it is so hidden within those
// components. Instead, we attach a global reference to the map
// on the window anytime this <MapWrapper /> component is mounted
// and it is removed on unmount. This allows non react-google-maps
// to access the google maps reference, see ImageOverlay

class MapWrapperBase extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      refReady: false,
      zoom: 19,
      mapTypeId: "satellite"
    };
    // The offset represents the amount to offset
    // based on the 475 mas-width'ed <PanelContainer>
    this.offset = (475 / 2) * -1;
  }
  panTo(center) {
    this.map.panTo(center);
    this.map.panBy(this.offset, 0);
  }
  componentDidMount() {
    window.MP_MAPEDIT_REF = this.map.context[MAP];
    this.setState({refReady: true});
    if (this.props.center) {
      this.panTo(this.props.center);
    }
  }
  componentWillUnmount() {
    window.MP_MAPEDIT_REF = null;
  }
  componentWillReceiveProps(nextProps) {
    // The following logic allows the user to pan around manually
    // and not have the map recenter on them all the time. This
    // method stops the recenter, unless a new center is explicitly
    // passed
    const nextCenter = nextProps.center;
    const currCenter = this.props.center;
    if (nextCenter && !currCenter) {
      this.panTo(nextCenter);
    } else if (nextCenter && (nextCenter.lat !== currCenter.lat || nextCenter.lng !== currCenter.lng)) {
      this.panTo(nextCenter);
    }
  }
  onZoomChanged = () => {
    this.setState({
      zoom: this.map.getZoom()
    });
  };
  onMapTypeIdChanged = () => {
    this.setState({
      mapTypeId: this.map.getMapTypeId()
    });
  };
  render() {
    const {zoom, mapTypeId, refReady} = this.state;

    return (
      <GoogleMap
        // Do not pass center or defaultCenter props. It is handled manually
        // by the componentWillReceiveProps method
        ref={map => {
          this.map = map;
        }}
        zoom={zoom}
        onZoomChanged={this.onZoomChanged}
        onMapTypeIdChanged={this.onMapTypeIdChanged}
        // You can only zoom to 20 on `satellite` mapTypeId
        // Force `roadmap` to allow for closer zoom
        mapTypeId={zoom >= 20 ? "roadmap" : mapTypeId}
        defaultOptions={mapConfig}>
        {refReady ? this.props.children : null}
      </GoogleMap>
    );
  }
}

const MapWrapper = withGoogleMap(MapWrapperBase);

export default class Map extends React.Component {
  render() {
    const {center, children} = this.props;
    return (
      <div id="Map">
        <MapWrapper
          containerElement={<div className="container" />}
          mapElement={<div className="map" />}
          center={arrayToPoint(center)}>
          {children}
        </MapWrapper>
      </div>
    );
  }
}

// The <MapPortalBase/> and <MapPortal/> are used in conjunction to allow
// the Google map to only be loaded once on /facility page load,
// but also allow the developer use logic and map components
// where it makes the most sense to use them. Every time the
// <MapWrapper> component loads, that is a request to the
// Google api, which costs money. So we want to load the map
// as little as possible. But, the map needs to be changed often
// to represent new centers and different children and we don't want
// to have to do all that logic here. The <MapPortal> is basically
// an event emitter. It is used like a component, which takes props and
// children, but actually doesn't return its children in place. It
// instead changes the state in the mapContainer by passing its props
// and children to the state. The <MapWrapper> is subscribed to that
// mapContainer state and renders the children/props there instead.
// So it is like a "portal". You can use the <MapPortal> component
// wherever it makes the most sense, for example as you work on a
// floor you can use a <MapPortal> with the floor center and
// appropriate <ImageOverlay> there, but the <ImageOverlay> will
// actually be rendered over in the <MapPortalBase>

// MapPortalBase listens to the mapContainer state and renders
// the props `component` from state as its children
export const MapPortalBase = props => {
  return (
    <Subscribe
      to={[mapContainer]}
      render={container => {
        const center = container.state.get("center");
        const component = container.state.get("component");
        return (
          <div id="MapPortalBase">
            <Map center={center}>{component}</Map>
          </div>
        );
      }}
    />
  );
};

// The MapPortal looks and acts like a normal component. You can use it
// wherever you want to do some logic and render some map children like
// a <MapEditMarker>, for example. This component does not actually
// render that <MapEditMarker> at all. But it setsState to the mapContainer,
// passing the <MapEditMarker>  which will be rendered there.
// Notice we do not get the `mapContainer` from a <Subscribe> here. We instead
// import and use it directly. It is important that there only ever be one
// instance of the `mapContainer`. If we try to get it off of the <Subscribe>
// here, we end up in an infinite loop
export const MapPortal = ({center, children: component}) => {
  // this is the source of the setState console error. The change will require
  // some thought since this is central to the App
  mapContainer.set({center, component});
  return null;
};



// WEBPACK FOOTER //
// ./src/components/Map/index.jsx